import { useState } from 'react';
import { X, Phone, Camera } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const galleryImages = [
    {
      src: 'https://customer-assets.emergentagent.com/job_web-setup-wizard/artifacts/bdmjfzpx_IMG_3438.jpeg',
      alt: 'New construction electrical rough-in wiring',
      category: 'New Construction',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_web-setup-wizard/artifacts/bblx8v10_IMG_3936.jpeg',
      alt: 'Electrical panel installation with breakers',
      category: 'Panel Work',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_web-setup-wizard/artifacts/fdhsc871_IMG_4168.jpeg',
      alt: 'Service entrance and meter pole installation',
      category: 'Service Upgrade',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_web-setup-wizard/artifacts/qkxy834n_IMG_3717.jpeg',
      alt: 'Vanity light fixture and outlet installation',
      category: 'Residential',
    },
    {
      src: 'https://customer-assets.emergentagent.com/job_web-setup-wizard/artifacts/bh7w714m_IMG_3910.jpeg',
      alt: 'Outlet testing - properly wired',
      category: 'Quality Check',
    },
  ];

  return (
    <div data-testid="gallery-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block bg-amber-600 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
              Our Work
            </span>
            <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl md:text-6xl font-bold uppercase text-white leading-tight mb-6">
              Project
              <span className="text-amber-500"> Gallery</span>
            </h1>
            <p className="text-xl text-slate-300">
              Browse our portfolio of completed electrical projects across residential, commercial, and industrial sectors.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section data-testid="gallery-grid" className="py-20 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                data-testid={`gallery-image-${index}`}
                onClick={() => setSelectedImage(image)}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <span className="inline-block bg-amber-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-2">
                    {image.category}
                  </span>
                  <p className="text-white font-medium">{image.alt}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Note about photos */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4">
              <Camera className="w-6 h-6 text-amber-600" />
              <p className="text-slate-700">
                <span className="font-semibold">More photos coming soon!</span>
                <span className="text-slate-500"> Check back for updates on our latest projects.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="gallery-cta" className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold uppercase text-white mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Whether it's a simple repair or a complete installation, we're here to help.
            Give us a call to discuss your electrical needs.
          </p>
          <a
            href="tel:+19047137611"
            data-testid="gallery-call-btn"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/25"
          >
            <Phone className="w-5 h-5" />
            Call (904) 713-7611
          </a>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none">
          <div className="relative">
            <button
              data-testid="lightbox-close-btn"
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-amber-500 transition-colors"
              aria-label="Close image"
            >
              <X className="w-8 h-8" />
            </button>
            {selectedImage && (
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="w-full h-auto"
                />
                <div className="p-4 bg-white">
                  <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-3 py-1 rounded-full mr-2">
                    {selectedImage.category}
                  </span>
                  <span className="text-slate-700 font-medium">{selectedImage.alt}</span>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GalleryPage;
